"use client";

import { useState } from "react";
import Link from "next/link";
import { API_BASE } from "@/lib/api-client";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

const ROLES = [
  { value: "law_firm", label: "Law firm" },
  { value: "litigation_funder", label: "Litigation funder" },
  { value: "other", label: "Other" },
] as const;

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [firm, setFirm] = useState("");
  const [role, setRole] = useState<"" | "law_firm" | "litigation_funder" | "other">("");
  const [message, setMessage] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status.kind === "submitting") return;
    setStatus({ kind: "submitting" });
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, firm, role, message, optIn }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          code?: string;
          message?: string;
          error?: string;
        };
        // The backend's VALIDATION_ERROR concatenates raw Zod issues
        // including internal enum values — never surface that to a
        // user. Native HTML5 validation should catch required + email
        // before we ever round-trip, so VALIDATION_ERROR here means
        // a corner the form didn't gate (e.g. message length). Show a
        // generic prompt and let the field markers do the rest.
        const friendly =
          data.code === "VALIDATION_ERROR"
            ? "Please check the form and try again."
            : data.message || data.error || "Something went wrong. Please try again.";
        setStatus({ kind: "error", message: friendly });
        return;
      }
      setStatus({ kind: "success" });
    } catch {
      setStatus({ kind: "error", message: "Network error. Please try again." });
    }
  }

  return (
    <section className="mx-auto w-full max-w-[1240px] flex-1 px-6 py-20 sm:px-10 sm:py-28">
      <div className="grid gap-16 lg:grid-cols-[1.05fr_1fr] lg:gap-24">
        <div className="max-w-[560px]">
          <span className="mb-7 inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent before:h-px before:w-6 before:bg-accent before:content-['']">
            Get in touch
          </span>
          <h1 className="mb-8 font-sans text-[44px] font-normal leading-[1.04] tracking-[-0.012em] text-foreground sm:text-[56px]">
            Let&apos;s talk.
          </h1>
          <p className="mb-6 max-w-[480px] text-[17px] leading-relaxed text-muted-foreground sm:text-lg">
            Tell us about your firm or fund and what you&apos;re working on.
            We&apos;ll come back with next steps within a few working days.
          </p>
          <p className="text-[14px] leading-relaxed text-muted-foreground">
            For teams working on complex disputes, collective actions,
            funded cases and high-value legal claims.
          </p>
        </div>

        <div>
          {status.kind === "success" ? (
            <SuccessState />
          ) : (
            <form
              onSubmit={onSubmit}
              className="grid gap-5 rounded-2xl border border-border bg-card p-7 sm:p-9"
            >
              <Field
                id="name"
                label="Your name"
                required
                value={name}
                onChange={setName}
                autoComplete="name"
              />
              <Field
                id="email"
                label="Work email"
                type="email"
                required
                value={email}
                onChange={setEmail}
                autoComplete="email"
                inputMode="email"
              />
              <Field
                id="firm"
                label="Firm or organisation"
                required
                value={firm}
                onChange={setFirm}
                autoComplete="organization"
              />
              <div className="grid gap-2">
                <label
                  htmlFor="role"
                  className="text-[13px] font-medium text-foreground"
                >
                  I am a
                  <span aria-hidden="true" className="text-accent">
                    {" "}*
                  </span>
                </label>
                <select
                  id="role"
                  name="role"
                  required
                  value={role}
                  onChange={(e) => setRole(e.target.value as typeof role)}
                  className="h-11 w-full rounded-lg border border-input bg-background px-3 text-[15px] text-foreground transition-colors focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                >
                  <option value="" disabled>
                    Select one
                  </option>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2">
                <label
                  htmlFor="message"
                  className="text-[13px] font-medium text-foreground"
                >
                  What are you working on?{" "}
                  <span className="font-normal text-muted-foreground">
                    (optional)
                  </span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="A few lines about the work, the firm, or the kind of opportunity you have in mind."
                  className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-[15px] leading-relaxed text-foreground transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                />
              </div>
              <label className="flex cursor-pointer items-start gap-3 text-[14px] leading-relaxed text-muted-foreground">
                <input
                  type="checkbox"
                  checked={optIn}
                  onChange={(e) => setOptIn(e.target.checked)}
                  className="mt-1 size-4 cursor-pointer rounded border-input accent-accent"
                />
                <span>
                  I&apos;d like to receive occasional product and research
                  updates from Thexis.
                </span>
              </label>

              {status.kind === "error" && (
                <p
                  role="alert"
                  className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
                >
                  {status.message}
                </p>
              )}

              <button
                type="submit"
                disabled={status.kind === "submitting"}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-[10px] bg-accent px-7 py-3.5 font-sans text-[15px] font-medium text-white transition-colors hover:bg-[#A94A22] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status.kind === "submitting" ? "Sending…" : "Send message →"}
              </button>
              <p className="text-[12px] leading-relaxed text-muted-foreground">
                By submitting this form you agree to be contacted by Thexis
                about your enquiry.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  type = "text",
  required = false,
  value,
  onChange,
  autoComplete,
  inputMode,
}: {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}) {
  return (
    <div className="grid gap-2">
      <label htmlFor={id} className="text-[13px] font-medium text-foreground">
        {label}
        {required && (
          <span aria-hidden="true" className="text-accent">
            {" "}*
          </span>
        )}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        inputMode={inputMode}
        className="h-11 w-full rounded-lg border border-input bg-background px-3 text-[15px] text-foreground transition-colors focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      />
    </div>
  );
}

function SuccessState() {
  return (
    <div className="rounded-2xl border border-border bg-card p-9 text-center">
      <span className="mb-5 inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-accent before:h-px before:w-6 before:bg-accent before:content-['']">
        Thanks for reaching out
      </span>
      <h2 className="mb-4 font-sans text-3xl font-normal leading-tight text-foreground sm:text-4xl">
        We&apos;ll be in touch.
      </h2>
      <p className="mx-auto mb-8 max-w-[420px] text-[15px] leading-relaxed text-muted-foreground">
        A member of the Thexis team will read your message and come back
        within a few working days.
      </p>
      <Link
        href="/"
        className="inline-flex items-center gap-2 border-b border-foreground py-2 font-sans text-[14px] font-medium text-foreground transition-colors hover:border-accent hover:text-accent"
      >
        Back to home
      </Link>
    </div>
  );
}
